import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  DatePicker,
  Divider,
  Input,
  InputNumber,
  Message,
  Modal,
  Panel,
  SelectPicker,
  Stack,
  TagPicker,
  toaster,
} from 'rsuite';

import {
  useCreateProject,
  useCreateTaskOrder,
  useRates,
  useUploadProjectPhoto,
  useUpdateTaskOrder,
  useSetProjectUsers,
  useTaskOrders,
  useUpdateProject,
  useUpsertUser,
  useUpsertUserRate,
  useUsers,
} from '../hooks/useQueries';

import { clearRsuitePickerSearchInputSoon } from '../utils/rsuitePickerSearch';

import './ProjectCreateWizardPage.css';

const normalizeIso10 = (raw) => {
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    const yyyy = raw.getFullYear();
    const mm = String(raw.getMonth() + 1).padStart(2, '0');
    const dd = String(raw.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  const s = String(raw ?? '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : null;
};

const parseMaybeIsoToDate = (raw) => {
  const s = normalizeIso10(raw);
  if (!s) return null;
  const d = new Date(`${s}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
};

const currencyInputFormatter = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return `$${n.toFixed(2)}`;
};

const currencyInputParser = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  return String(value).replace(/[^0-9.-]/g, '');
};

const budgetCurrencyFormatter = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return n.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const normalizeBudgetDigits = (raw) => {
  const digits = String(raw ?? '').replace(/[^0-9]/g, '');
  return digits;
};

const parseBudgetNumber = (raw) => {
  const digits = normalizeBudgetDigits(raw);
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) ? n : null;
};

const rateCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const normalizeApiErrorMessage = (error) => {
  const raw = String(error?.message || error || '').trim();
  if (!raw) return 'Request failed.';

  const m = raw.match(/^HTTP\s+(\d+)\s*:\s*(.*)$/i);
  if (!m) return raw;

  const payload = String(m[2] || '').trim();
  try {
    const parsed = JSON.parse(payload);
    const apiMsg = String(parsed?.error || parsed?.message || '').trim();
    if (apiMsg) return apiMsg;
  } catch {
    // ignore JSON parsing failures
  }

  return payload || raw;
};

const isHttp409Error = (error) => {
  const msg = String(error?.message || error || '');
  return /HTTP\s*409\b/i.test(msg);
};

const WizardError = ({ error }) => {
  if (!error) return null;
  if (isHttp409Error(error)) return null;
  return (
    <Message type="error" showIcon>
      {normalizeApiErrorMessage(error)}
    </Message>
  );
};

export default function ProjectCreateWizardPage() {
  const navigate = useNavigate();

  const { data: users = [], isLoading: usersLoading, error: usersError } = useUsers();
  const { data: ratesRes = [] } = useRates(null);

  const createProject = useCreateProject();
  const createTaskOrder = useCreateTaskOrder();
  const updateTaskOrder = useUpdateTaskOrder();
  const updateProject = useUpdateProject();
  const uploadProjectPhoto = useUploadProjectPhoto({
    onSuccess: async () => {
      toaster.push(
        <Message type="success" showIcon closable>
          Project logo uploaded.
        </Message>,
        { placement: 'topEnd', duration: 2200 }
      );
    },
  });
  const setProjectUsers = useSetProjectUsers();
  const upsertUser = useUpsertUser();
  const upsertUserRate = useUpsertUserRate();

  const [step, setStep] = useState(0);
  const [projectId, setProjectId] = useState(null);

  // Step 1
  const [name, setName] = useState('');
  const [contractNumber, setContractNumber] = useState('');
  const [picUserId, setPicUserId] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedLogoFile, setSelectedLogoFile] = useState(null);
  const [selectedLogoPreviewUrl, setSelectedLogoPreviewUrl] = useState('');
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState('');

  // Step 2
  const [budgetTarget, setBudgetTarget] = useState('');

  // Step 3
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const projectMembersPickerRef = useRef(null);

  // Step 4
  const [ratesByUserId, setRatesByUserId] = useState({});
  const [effectiveDateByUserId, setEffectiveDateByUserId] = useState({});

  // Step 3 (Task Orders)
  const [addTaskOrderOpen, setAddTaskOrderOpen] = useState(false);
  const [taskOrderModalMode, setTaskOrderModalMode] = useState('create'); // 'create' | 'edit'
  const [editingTaskOrderId, setEditingTaskOrderId] = useState(null);
  const [newTaskOrderName, setNewTaskOrderName] = useState('');
  const [newTaskOrderStartDate, setNewTaskOrderStartDate] = useState(null);
  const [newTaskOrderEndDate, setNewTaskOrderEndDate] = useState(null);
  const [newTaskOrderBudget, setNewTaskOrderBudget] = useState('');

  const userOptions = useMemo(
    () =>
      (users || []).map((u) => ({
        label: String(u?.name || '—'),
        value: Number(u?.id),
        ftePercent: Number.isFinite(Number(u?.ftePercent)) ? Number(u.ftePercent) : 100,
      })),
    [users]
  );
  const highestRateByUserId = useMemo(() => {
    const rows = Array.isArray(ratesRes)
      ? ratesRes
      : Array.isArray(ratesRes?.rows)
        ? ratesRes.rows
        : [];
    const map = new Map();

    for (const r of rows) {
      const userId = Number(r?.userId);
      const rate = Number(r?.rate);
      if (!Number.isInteger(userId) || !Number.isFinite(rate)) continue;

      const effectiveDate = String(r?.effectiveDate || '').slice(0, 10);
      const projectName = String(r?.projectName || '').trim() || 'Unknown project';
      const prev = map.get(userId);

      if (!prev || rate > prev.rate || (rate === prev.rate && effectiveDate.localeCompare(String(prev.effectiveDate || '')) > 0)) {
        map.set(userId, {
          rate,
          projectName,
          effectiveDate,
        });
      }
    }

    return map;
  }, [ratesRes]);

  const memberPickerOptions = useMemo(() => {
    return (users || []).map((u) => {
      const userId = Number(u?.id);
      const userName = String(u?.name || '—');
      const utilization = Number.isFinite(Number(u?.ftePercent)) ? Number(u.ftePercent) : 100;
      const topRate = highestRateByUserId.get(userId);

      return {
        label: userName,
        value: String(userId),
        utilization,
        highestRateText: topRate ? rateCurrencyFormatter.format(Number(topRate.rate)) : null,
        highestRateProjectName: topRate?.projectName || null,
      };
    });
  }, [highestRateByUserId, users]);
  const userNameById = useMemo(() => {
    const map = new Map();
    for (const u of users || []) {
      const id = Number(u?.id);
      if (!Number.isInteger(id)) continue;
      map.set(id, u?.name || '—');
    }
    return map;
  }, [users]);

  const safeProjectId = Number.isInteger(projectId) ? projectId : null;

  const step1Valid = useMemo(() => {
    const n = String(name || '').trim();
    if (!n) return false;
    const s = normalizeIso10(startDate);
    const e = normalizeIso10(endDate);
    if (!s || !e) return false;
    if (String(e).localeCompare(String(s)) < 0) return false;
    return true;
  }, [name, startDate, endDate]);

  const projectStartIso = useMemo(() => normalizeIso10(startDate), [startDate]);
  const projectEndIso = useMemo(() => normalizeIso10(endDate), [endDate]);
  const shouldDisableOutsideProjectRange = useMemo(() => {
    if (!projectStartIso || !projectEndIso) return () => false;
    return (date) => {
      const iso = normalizeIso10(date);
      if (!iso) return false;
      return String(iso).localeCompare(String(projectStartIso)) < 0 || String(iso).localeCompare(String(projectEndIso)) > 0;
    };
  }, [projectStartIso, projectEndIso]);

  const budgetValueNumber = useMemo(() => {
    return parseBudgetNumber(budgetTarget);
  }, [budgetTarget]);

  const handleBudgetTargetChange = (next) => {
    const digits = normalizeBudgetDigits(next);
    if (!digits) {
      setBudgetTarget('');
      return;
    }
    setBudgetTarget(budgetCurrencyFormatter(digits));
  };

  const handleNewTaskOrderBudgetChange = (next) => {
    const digits = normalizeBudgetDigits(next);
    if (!digits) {
      setNewTaskOrderBudget('');
      return;
    }
    setNewTaskOrderBudget(budgetCurrencyFormatter(digits));
  };

  const step2Valid = useMemo(() => Number.isFinite(budgetValueNumber) && Number(budgetValueNumber) >= 0, [budgetValueNumber]);

  const step3Valid = useMemo(() => Array.isArray(selectedUserIds) && selectedUserIds.length > 0, [selectedUserIds]);

  const step4Valid = useMemo(() => {
    if (!Number.isInteger(safeProjectId)) return false;
    if (!Array.isArray(selectedUserIds) || selectedUserIds.length === 0) return false;

    for (const uid of selectedUserIds) {
      const rate = Number(ratesByUserId?.[uid]);
      const eff = normalizeIso10(effectiveDateByUserId?.[uid]);
      if (!Number.isFinite(rate) || rate <= 0) return false;
      if (!eff) return false;
    }

    return true;
  }, [safeProjectId, selectedUserIds, ratesByUserId, effectiveDateByUserId]);

  const isBusy =
    createProject.isPending ||
    createTaskOrder.isPending ||
    updateTaskOrder.isPending ||
    updateProject.isPending ||
    uploadProjectPhoto.isPending ||
    setProjectUsers.isPending ||
    upsertUser.isPending ||
    upsertUserRate.isPending;

  useEffect(() => {
    return () => {
      if (selectedLogoPreviewUrl) {
        try {
          URL.revokeObjectURL(selectedLogoPreviewUrl);
        } catch {
          // no-op
        }
      }
    };
  }, [selectedLogoPreviewUrl]);

  const onSelectLogoFile = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!String(file.type || '').startsWith('image/')) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Please select an image file.
        </Message>,
        { placement: 'topEnd' }
      );
      if (event?.target) event.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toaster.push(
        <Message showIcon type="warning" closable>
          Image is too large (max 5MB).
        </Message>,
        { placement: 'topEnd' }
      );
      if (event?.target) event.target.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    if (selectedLogoPreviewUrl) {
      try {
        URL.revokeObjectURL(selectedLogoPreviewUrl);
      } catch {
        // no-op
      }
    }

    setSelectedLogoFile(file);
    setSelectedLogoPreviewUrl(objectUrl);
    if (event?.target) event.target.value = '';
  };

  const {
    data: taskOrders = [],
    isLoading: taskOrdersLoading,
    error: taskOrdersError,
  } = useTaskOrders(safeProjectId, { enabled: Number.isInteger(safeProjectId) });

  const nonDefaultTaskOrders = useMemo(() => {
    return (Array.isArray(taskOrders) ? taskOrders : []).filter((o) => !o?.isDefault);
  }, [taskOrders]);

  useEffect(() => {
    // Keep PIC in the selected list by default.
    const pid = Number(picUserId);
    if (!Number.isInteger(pid) || pid <= 0) return;
    setSelectedUserIds((prev) => {
      const set = new Set((prev || []).map((v) => Number(v)).filter((n) => Number.isInteger(n)));
      set.add(pid);
      return Array.from(set);
    });
  }, [picUserId]);

  useEffect(() => {
    // Seed effective dates for new selections.
    const defaultEff = normalizeIso10(startDate) || normalizeIso10(new Date());
    if (!defaultEff) return;

    setEffectiveDateByUserId((prev) => {
      const next = { ...(prev || {}) };
      for (const uid of selectedUserIds || []) {
        if (!next[uid]) next[uid] = defaultEff;
      }
      return next;
    });
  }, [selectedUserIds, startDate]);

  const goBackToProjects = () => navigate('/', { replace: true });

  const submitStep1 = async () => {
    if (!step1Valid) return;

    const trimmed = String(name || '').trim();
    const contractNumberTrimmed = String(contractNumber || '').trim();
    const startIso = normalizeIso10(startDate);
    const endIso = normalizeIso10(endDate);

    const pic = Number.isInteger(Number(picUserId)) ? Number(picUserId) : null;

    try {
      // Create project once, then update remaining fields.
      let pid = safeProjectId;
      if (!Number.isInteger(pid)) {
        const created = await createProject.mutateAsync({
          name: trimmed,
          picUserId: pic,
          contractNumber: contractNumberTrimmed || null,
        });
        const createdId = Number(created?.id);
        if (Number.isInteger(createdId)) {
          pid = createdId;
          setProjectId(createdId);
        }
        toaster.push(
          <Message type="success" showIcon closable>
            Project created.
          </Message>,
          { placement: 'topEnd', duration: 2200 }
        );
      }

      if (Number.isInteger(pid)) {
        await updateProject.mutateAsync({
          projectId: pid,
          payload: {
            name: trimmed,
            picUserId: pic,
            contractNumber: contractNumberTrimmed || null,
            startDate: startIso,
            endDate: endIso,
          },
        });

        if (selectedLogoFile) {
          const toBase64 = () =>
            new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => {
                const full = String(reader.result || '');
                const commaIdx = full.indexOf(',');
                const b64 = commaIdx >= 0 ? full.slice(commaIdx + 1) : full;
                resolve(b64);
              };
              reader.onerror = () => reject(new Error('Failed to read file'));
              reader.readAsDataURL(selectedLogoFile);
            });

          const dataBase64 = await toBase64();
          const uploaded = await uploadProjectPhoto.mutateAsync({
            projectId: pid,
            payload: {
              fileName: selectedLogoFile.name,
              mimeType: selectedLogoFile.type || 'application/octet-stream',
              dataBase64,
            },
          });
          setUploadedLogoUrl(String(uploaded?.photoUrl || ''));
          setSelectedLogoFile(null);
          if (selectedLogoPreviewUrl) {
            try {
              URL.revokeObjectURL(selectedLogoPreviewUrl);
            } catch {
              // no-op
            }
          }
          setSelectedLogoPreviewUrl('');
        }
      }

      setStep(1);
    } catch (err) {
      const msg = normalizeApiErrorMessage(err);
      const isDuplicateName =
        msg.includes('A project with this name already exists') ||
        msg.includes('projects_name_uq') ||
        msg.includes('duplicate key value');

      toaster.push(
        <Message type="error" showIcon closable>
          {isDuplicateName ? 'A project with this name already exists.' : `Failed to create project: ${msg}`}
        </Message>,
        { placement: 'topEnd', duration: isDuplicateName ? 3000 : 6000 }
      );
    }
  };

  const submitStep2 = async () => {
    if (!Number.isInteger(safeProjectId)) return;
    if (!step2Valid) return;

    await updateProject.mutateAsync({
      projectId: safeProjectId,
      payload: { budgetTarget: budgetValueNumber },
    });

    setStep(2);
  };

  const submitStep3 = async () => {
    if (!Number.isInteger(safeProjectId)) return;
    // Task orders are optional; Default will always exist.
    setStep(3);
  };

  const submitStep4 = async () => {
    if (!Number.isInteger(safeProjectId)) return;
    if (!step3Valid) return;

    const ids = Array.from(
      new Set(
        (selectedUserIds || [])
          .map((v) => Number(v))
          .filter((n) => Number.isInteger(n) && n > 0)
      )
    );

    await setProjectUsers.mutateAsync({ projectId: safeProjectId, userIds: ids });

    toaster.push(
      <Message type="success" showIcon closable>
        Project members saved.
      </Message>,
      { placement: 'topEnd', duration: 2200 }
    );

    setStep(4);
  };

  const handleCreateUserFromPicker = async (_value, item) => {
    const label = String(item?.label ?? item?.value ?? '').trim();
    if (!label) return;

    try {
      const created = await upsertUser.mutateAsync({ name: label, ftePercent: 100 });
      const id = Number(created?.id);
      if (Number.isInteger(id) && id > 0) {
        setSelectedUserIds((prev) => Array.from(new Set([...(prev || []), id])));
      }
      toaster.push(
        <Message type="success" showIcon closable>
          User added.
        </Message>,
        { placement: 'topEnd', duration: 2200 }
      );
    } catch (err) {
      toaster.push(
        <Message type="error" showIcon closable>
          Failed to create user: {String(err?.message || err)}
        </Message>,
        { placement: 'topEnd' }
      );
    }
  };

  const submitStep5 = async () => {
    if (!Number.isInteger(safeProjectId)) return;
    if (!step4Valid) return;

    const pid = safeProjectId;

    await Promise.all(
      (selectedUserIds || [])
        .map((v) => Number(v))
        .filter((n) => Number.isInteger(n) && n > 0)
        .map(async (userId) => {
          const rate = Number(ratesByUserId?.[userId]);
          const effectiveDate = normalizeIso10(effectiveDateByUserId?.[userId]);
          await upsertUserRate.mutateAsync({ userId, projectId: pid, rate, effectiveDate });
        })
    );

    toaster.push(
      <Message type="success" showIcon closable>
        Rates saved.
      </Message>,
      { placement: 'topEnd', duration: 2200 }
    );

    navigate(`/projects/${pid}`, { replace: true });
  };

  const resetTaskOrderModal = () => {
    setTaskOrderModalMode('create');
    setEditingTaskOrderId(null);
    setNewTaskOrderName('');
    setNewTaskOrderStartDate(null);
    setNewTaskOrderEndDate(null);
    setNewTaskOrderBudget('');
  };

  const openCreateTaskOrderModal = () => {
    resetTaskOrderModal();
    setTaskOrderModalMode('create');
    setAddTaskOrderOpen(true);
  };

  const openEditTaskOrderModal = (taskOrder) => {
    const id = Number(taskOrder?.id);
    if (!Number.isInteger(id)) return;
    setTaskOrderModalMode('edit');
    setEditingTaskOrderId(id);
    setNewTaskOrderName(String(taskOrder?.name ?? ''));
    setNewTaskOrderStartDate(taskOrder?.startDate ?? null);
    setNewTaskOrderEndDate(taskOrder?.endDate ?? null);
    setNewTaskOrderBudget(
      taskOrder?.budgetTarget === null || taskOrder?.budgetTarget === undefined
        ? ''
        : budgetCurrencyFormatter(taskOrder.budgetTarget)
    );
    setAddTaskOrderOpen(true);
  };

  const canSubmitNewTaskOrder = useMemo(() => {
    const name = String(newTaskOrderName || '').trim();
    if (!name) return false;
    const s = normalizeIso10(newTaskOrderStartDate);
    const e = normalizeIso10(newTaskOrderEndDate);
    if (!s || !e) return false;
    if (String(e).localeCompare(String(s)) < 0) return false;
    if (projectStartIso && String(s).localeCompare(String(projectStartIso)) < 0) return false;
    if (projectEndIso && String(e).localeCompare(String(projectEndIso)) > 0) return false;
    const budget = parseBudgetNumber(newTaskOrderBudget);
    if (budget === null) return false;
    if (!Number.isFinite(budget) || budget < 0) return false;

    // If project budget is set, ensure we don't exceed remaining budget.
    if (Number.isFinite(Number(budgetValueNumber))) {
      const total = Number(budgetValueNumber);
      const editingId = taskOrderModalMode === 'edit' ? Number(editingTaskOrderId) : null;
      const allocatedExcludingEditing = (nonDefaultTaskOrders || []).reduce((sum, o) => {
        const id = Number(o?.id);
        if (Number.isInteger(editingId) && id === editingId) return sum;
        const b = o?.budgetTarget;
        const n = b === null || b === undefined ? 0 : Number(b);
        return Number.isFinite(n) ? sum + n : sum;
      }, 0);
      const remaining = total - allocatedExcludingEditing;
      if (budget > remaining) return false;
    }

    return true;
  }, [
    newTaskOrderName,
    newTaskOrderStartDate,
    newTaskOrderEndDate,
    newTaskOrderBudget,
    projectStartIso,
    projectEndIso,
    budgetValueNumber,
    nonDefaultTaskOrders,
    taskOrderModalMode,
    editingTaskOrderId,
  ]);

  const allocatedBudget = useMemo(() => {
    return (nonDefaultTaskOrders || []).reduce((sum, o) => {
      const b = o?.budgetTarget;
      const n = b === null || b === undefined ? 0 : Number(b);
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);
  }, [nonDefaultTaskOrders]);

  const remainingBudgetForNew = useMemo(() => {
    if (!Number.isFinite(Number(budgetValueNumber))) return null;
    const total = Number(budgetValueNumber);
    return total - allocatedBudget;
  }, [budgetValueNumber, allocatedBudget]);

  const remainingBudgetForEdit = useMemo(() => {
    if (!Number.isFinite(Number(budgetValueNumber))) return null;
    const total = Number(budgetValueNumber);
    const editingId = Number(editingTaskOrderId);
    const allocatedExcludingEditing = (nonDefaultTaskOrders || []).reduce((sum, o) => {
      const id = Number(o?.id);
      if (Number.isInteger(editingId) && id === editingId) return sum;
      const b = o?.budgetTarget;
      const n = b === null || b === undefined ? 0 : Number(b);
      return Number.isFinite(n) ? sum + n : sum;
    }, 0);
    return total - allocatedExcludingEditing;
  }, [budgetValueNumber, editingTaskOrderId, nonDefaultTaskOrders]);

  const remainingBudget = taskOrderModalMode === 'edit' ? remainingBudgetForEdit : remainingBudgetForNew;
  const budgetEntered = useMemo(() => parseBudgetNumber(newTaskOrderBudget), [newTaskOrderBudget]);
  const remainingAfterEntered = useMemo(() => {
    if (remainingBudget === null) return null;
    const entered = budgetEntered === null ? 0 : Number(budgetEntered);
    return Number(remainingBudget) - (Number.isFinite(entered) ? entered : 0);
  }, [remainingBudget, budgetEntered]);

  const shouldDisableStartDate = useMemo(() => {
    const base = shouldDisableOutsideProjectRange;
    const endIso = normalizeIso10(newTaskOrderEndDate);
    if (!endIso) return base;
    return (date) => {
      if (base(date)) return true;
      const iso = normalizeIso10(date);
      if (!iso) return false;
      return String(iso).localeCompare(String(endIso)) > 0;
    };
  }, [shouldDisableOutsideProjectRange, newTaskOrderEndDate]);

  const shouldDisableEndDate = useMemo(() => {
    const base = shouldDisableOutsideProjectRange;
    const startIso = normalizeIso10(newTaskOrderStartDate);
    if (!startIso) return base;
    return (date) => {
      if (base(date)) return true;
      const iso = normalizeIso10(date);
      if (!iso) return false;
      return String(iso).localeCompare(String(startIso)) < 0;
    };
  }, [shouldDisableOutsideProjectRange, newTaskOrderStartDate]);

  const submitNewTaskOrder = async () => {
    if (!Number.isInteger(safeProjectId)) return;
    if (!canSubmitNewTaskOrder) return;

    const name = String(newTaskOrderName || '').trim();
    const startIso = normalizeIso10(newTaskOrderStartDate);
    const endIso = normalizeIso10(newTaskOrderEndDate);
    const budgetTarget = parseBudgetNumber(newTaskOrderBudget);

    if (taskOrderModalMode === 'edit') {
      const toid = Number(editingTaskOrderId);
      if (!Number.isInteger(toid)) return;
      await updateTaskOrder.mutateAsync({
        projectId: safeProjectId,
        taskOrderId: toid,
        payload: {
          name,
          startDate: startIso,
          endDate: endIso,
          budgetTarget,
        },
      });
    } else {
      await createTaskOrder.mutateAsync({
        projectId: safeProjectId,
        payload: {
          name,
          startDate: startIso,
          endDate: endIso,
          budgetTarget,
        },
      });
    }

    toaster.push(
      <Message type="success" showIcon closable>
        {taskOrderModalMode === 'edit' ? 'Task order updated.' : 'Task order created.'}
      </Message>,
      { placement: 'topEnd', duration: 2200 }
    );

    setAddTaskOrderOpen(false);
    resetTaskOrderModal();
  };

  const stepItems = [
    { title: 'Details' },
    { title: 'Budget' },
    { title: 'Task Orders' },
    { title: 'People' },
    { title: 'Rates' },
  ];

  return (
    <div className="ppc-project-wizard">
      <Panel
        bordered
        header={
          <Stack justifyContent="space-between" alignItems="center" wrap>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#666' }}>Create project</div>
              <div style={{ fontSize: 18, fontWeight: 800, lineHeight: '22px' }}>New Project Wizard</div>
            </div>
            <Stack spacing={10} wrap>
              <Button appearance="ghost" onClick={goBackToProjects} disabled={isBusy}>
                Back to projects
              </Button>
            </Stack>
          </Stack>
        }
      >
        <div className="ppc-project-wizard__flow" aria-label="Project wizard process flow">
          {stepItems.map((s, index) => {
            const isDone = index < step;
            const isCurrent = index === step;
            return (
              <div
                key={s.title}
                className={`ppc-project-wizard__flow-item${isDone ? ' is-done' : ''}${isCurrent ? ' is-current' : ''}`}
              >
                <div className="ppc-project-wizard__flow-node">{index + 1}</div>
                <div className="ppc-project-wizard__flow-label">{s.title}</div>
              </div>
            );
          })}
        </div>

        <Divider />

        {usersError ? <Message type="error" showIcon>Failed to load users.</Message> : null}

        <div className="ppc-project-wizard__body">
          {step === 0 ? (
            <Panel bordered header={<span>1) Project details</span>}>
              <div className="ppc-project-wizard__grid">
                <div className="ppc-project-wizard__row">
                  <div>
                    <div className="ppc-project-wizard__label">Project name</div>
                    <Input value={name} onChange={setName} placeholder="e.g. ProPlan 2026" />
                  </div>

                  <div>
                    <div className="ppc-project-wizard__label">Contract Number</div>
                    <Input
                      value={contractNumber}
                      onChange={setContractNumber}
                      placeholder="e.g. CN-2026-001"
                    />
                  </div>
                </div>

                <div className="ppc-project-wizard__row">
                  <div>
                    <div className="ppc-project-wizard__label">Start date</div>
                    <DatePicker
                      oneTap
                      format="yyyy-MM-dd"
                      value={parseMaybeIsoToDate(startDate) || startDate}
                      onChange={setStartDate}
                      placeholder="YYYY-MM-DD"
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <div className="ppc-project-wizard__label">End date</div>
                    <DatePicker
                      oneTap
                      format="yyyy-MM-dd"
                      value={parseMaybeIsoToDate(endDate) || endDate}
                      onChange={setEndDate}
                      placeholder="YYYY-MM-DD"
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>

                <div className="ppc-project-wizard__row">
                  <div>
                    <div className="ppc-project-wizard__label">PIC</div>
                    <SelectPicker
                      cleanable
                      searchable
                      placeholder={usersLoading ? 'Loading…' : 'Select a PIC'}
                      data={userOptions}
                      renderMenuItem={(label, item) => {
                        const userId = Number(item?.value);
                        const util = Number.isFinite(Number(item?.ftePercent)) ? Number(item.ftePercent) : 100;
                        const topRate = highestRateByUserId.get(userId);
                        const rateText = topRate ? rateCurrencyFormatter.format(Number(topRate.rate)) : null;
                        const rateProject = topRate?.projectName || null;

                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <div style={{ fontWeight: 700, lineHeight: '16px' }}>{label}</div>
                            <div style={{ fontSize: 11, color: '#666', lineHeight: '14px' }}>
                              Utilization: {util}%
                              {rateText && rateProject ? ` • Highest rate: ${rateText} (${rateProject})` : ''}
                            </div>
                          </div>
                        );
                      }}
                      value={Number.isInteger(Number(picUserId)) ? Number(picUserId) : null}
                      onChange={(v) => {
                        if (v === null || v === undefined || v === '') {
                          setPicUserId(null);
                          return;
                        }
                        const n = Number(v);
                        setPicUserId(Number.isInteger(n) ? n : null);
                      }}
                      disabled={usersLoading}
                      style={{ width: '100%' }}
                    />
                  </div>

                  <div>
                    <div className="ppc-project-wizard__label">Logo</div>
                    <Stack spacing={10} alignItems="center" wrap>
                      <div className="ppc-project-wizard__logo-preview">
                        {selectedLogoPreviewUrl || uploadedLogoUrl ? (
                          <img
                            src={selectedLogoPreviewUrl || uploadedLogoUrl}
                            alt="Project logo preview"
                            className="ppc-project-wizard__logo-image"
                          />
                        ) : (
                          <span className="ppc-project-wizard__logo-placeholder">No logo</span>
                        )}
                      </div>

                      <label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={onSelectLogoFile}
                          style={{ display: 'none' }}
                          disabled={isBusy}
                        />
                        <Button as="span" appearance="ghost" size="sm" disabled={isBusy}>
                          Upload
                        </Button>
                      </label>

                      {(selectedLogoPreviewUrl || uploadedLogoUrl) ? (
                        <Button
                          appearance="ghost"
                          color="red"
                          size="sm"
                          disabled={isBusy}
                          onClick={() => {
                            setSelectedLogoFile(null);
                            if (selectedLogoPreviewUrl) {
                              try {
                                URL.revokeObjectURL(selectedLogoPreviewUrl);
                              } catch {
                                // no-op
                              }
                            }
                            setSelectedLogoPreviewUrl('');
                            setUploadedLogoUrl('');
                          }}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </Stack>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>PNG/JPG/WebP up to 5MB.</div>
                  </div>
                </div>
              </div>

              {!step1Valid ? (
                <div style={{ marginTop: 12 }}>
                  <Message type="warning" showIcon>
                    Enter a name and valid start/end dates.
                  </Message>
                </div>
              ) : null}

              <div className="ppc-project-wizard__footer">
                <WizardError error={createProject.error || updateProject.error} />
                <Stack justifyContent="flex-end" spacing={10}>
                  <Button appearance="primary" onClick={submitStep1} loading={isBusy} disabled={!step1Valid || isBusy}>
                    {Number.isInteger(safeProjectId) ? 'Save & Continue' : 'Create & Continue'}
                  </Button>
                </Stack>
              </div>
            </Panel>
          ) : null}

          {step === 1 ? (
            <Panel bordered header={<span>2) Total budget</span>}>
              <div className="ppc-project-wizard__budget-center">
                <Input
                  className="ppc-project-wizard__budget-input"
                  style={{ width: 260 }}
                  value={budgetTarget}
                  onChange={handleBudgetTargetChange}
                  placeholder="$0"
                  inputMode="numeric"
                />
              </div>

              <div className="ppc-project-wizard__footer">
                <WizardError error={updateProject.error} />
                <Stack justifyContent="space-between" spacing={10}>
                  <Button appearance="subtle" onClick={() => setStep(0)} disabled={isBusy}>
                    Back
                  </Button>
                  <Button appearance="primary" onClick={submitStep2} loading={isBusy} disabled={!step2Valid || isBusy}>
                    Save & Continue
                  </Button>
                </Stack>
              </div>
            </Panel>
          ) : null}

          {step === 2 ? (
            <Panel
              bordered
              header={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <span>3) Task orders</span>
                  <Button
                    appearance="ghost"
                    size="sm"
                    onClick={() => {
                      openCreateTaskOrderModal();
                    }}
                    disabled={!Number.isInteger(safeProjectId) || isBusy}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      Add new
                      <i className="bi bi-plus-lg" aria-hidden="true" />
                    </span>
                  </Button>
                </div>
              }
            >
              {!Number.isInteger(safeProjectId) ? (
                <Message type="info" showIcon>
                  Create the project first.
                </Message>
              ) : null}

              {taskOrdersError ? (
                <Message type="error" showIcon>
                  Failed to load task orders.
                </Message>
              ) : null}

              {taskOrdersLoading ? <div>Loading task orders…</div> : null}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {nonDefaultTaskOrders.length === 0 ? (
                  <Message type="info" showIcon>
                    No task orders yet. If you skip this step, the project will use a single implicit “Default” task order.
                  </Message>
                ) : (
                  nonDefaultTaskOrders.map((o) => (
                    <div
                      key={String(o?.id)}
                      style={{
                        border: '1px solid #e9ecef',
                        borderRadius: 10,
                        padding: '10px 12px',
                        background: '#fbfbfc',
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: 10,
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800, lineHeight: '18px' }}>{o?.name || '—'}</div>
                        <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                          {o?.startDate && o?.endDate ? `${o.startDate} → ${o.endDate}` : 'Dates not set'}
                          {o?.budgetTarget !== null && o?.budgetTarget !== undefined
                            ? ` • Budget ${budgetCurrencyFormatter(o.budgetTarget)}`
                            : ''}
                        </div>
                      </div>
                      <Button
                        appearance="subtle"
                        size="xs"
                        onClick={() => openEditTaskOrderModal(o)}
                        disabled={isBusy}
                      >
                        Edit
                      </Button>
                    </div>
                  ))
                )}
              </div>

              <div className="ppc-project-wizard__footer">
                <WizardError error={createTaskOrder.error} />
                <Stack justifyContent="space-between" spacing={10}>
                  <Button appearance="subtle" onClick={() => setStep(1)} disabled={isBusy}>
                    Back
                  </Button>
                  <Button appearance="primary" onClick={submitStep3} loading={isBusy} disabled={isBusy}>
                    Save & Continue
                  </Button>
                </Stack>
              </div>
            </Panel>
          ) : null}

          {step === 3 ? (
            <Panel bordered header={<span>4) Assign people</span>}>
              <div>
                <div className="ppc-project-wizard__label">Project members</div>
                <TagPicker
                  ref={projectMembersPickerRef}
                  block
                  searchable
                  cleanable={false}
                  creatable
                  loading={usersLoading || upsertUser.isPending}
                  placeholder={usersLoading ? 'Loading…' : 'Type to create new user'}
                  data={memberPickerOptions
                    .filter((u) => u.value && u.value !== '0')}
                  renderMenuItem={(label, item) => {
                    const util = Number.isFinite(Number(item?.utilization)) ? Number(item.utilization) : 100;
                    const rateText = item?.highestRateText;
                    const projectName = item?.highestRateProjectName;

                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                        <div style={{ fontWeight: 700, lineHeight: '16px' }}>{label}</div>
                        <div style={{ fontSize: 11, color: '#666', lineHeight: '14px' }}>
                          Utilization: {util}%
                          {rateText && projectName ? ` • Highest rate: ${rateText} (${projectName})` : ' • Highest rate: —'}
                        </div>
                      </div>
                    );
                  }}
                  value={(selectedUserIds || []).map((id) => String(id))}
                  onChange={(vals) => {
                    const raw = Array.isArray(vals) ? vals : [];
                    setSelectedUserIds(
                      Array.from(
                        new Set(
                          raw
                            .map((v) => Number(v))
                            .filter((n) => Number.isInteger(n) && n > 0)
                        )
                      )
                    );
                    clearRsuitePickerSearchInputSoon(projectMembersPickerRef);
                  }}
                  onCreate={(v) => {
                    clearRsuitePickerSearchInputSoon(projectMembersPickerRef);
                    handleCreateUserFromPicker(v);
                  }}
                  onSelect={() => {
                    clearRsuitePickerSearchInputSoon(projectMembersPickerRef);
                  }}
                  disabled={usersLoading}
                />

                {!step3Valid ? (
                  <div style={{ marginTop: 10 }}>
                    <Message type="warning" showIcon>
                      Select at least one person.
                    </Message>
                  </div>
                ) : null}
              </div>

              <div className="ppc-project-wizard__footer">
                <WizardError error={setProjectUsers.error} />
                <Stack justifyContent="space-between" spacing={10}>
                  <Button appearance="subtle" onClick={() => setStep(2)} disabled={isBusy}>
                    Back
                  </Button>
                  <Button appearance="primary" onClick={submitStep4} loading={isBusy} disabled={!step3Valid || isBusy}>
                    Save & Continue
                  </Button>
                </Stack>
              </div>
            </Panel>
          ) : null}

          {step === 4 ? (
            <Panel bordered header={<span>5) Assign rates</span>}>
              <div style={{ marginBottom: 10, color: '#666', fontSize: 12 }}>
                Set rates for everyone on this project. Project is already selected.
              </div>

              <div className="ppc-project-wizard__rates">
                {(selectedUserIds || []).map((uid) => {
                  const userId = Number(uid);
                  const userName = userNameById.get(userId) || '—';

                  const rateVal = ratesByUserId?.[userId] ?? '';
                  const effVal = effectiveDateByUserId?.[userId] ?? normalizeIso10(startDate) ?? normalizeIso10(new Date());

                  const hasRate = Number.isFinite(Number(rateVal)) && Number(rateVal) > 0;
                  const hasEff = Boolean(normalizeIso10(effVal));

                  return (
                    <div key={String(userId)} className="ppc-project-wizard__rate-row">
                      <div className="ppc-project-wizard__rate-user">
                        <div style={{ fontWeight: 700, lineHeight: '18px' }}>{userName}</div>
                        <div style={{ fontSize: 12, color: '#777' }}>User #{userId}</div>
                      </div>

                      <div className="ppc-project-wizard__rate-input">
                        <Stack spacing={10} alignItems="center" wrap>
                          <InputNumber
                            style={{ width: 160 }}
                            min={0}
                            step={1}
                            precision={2}
                            value={rateVal}
                            onChange={(v) =>
                              setRatesByUserId((prev) => ({
                                ...(prev || {}),
                                [userId]: v,
                              }))
                            }
                            formatter={currencyInputFormatter}
                            parser={currencyInputParser}
                            placeholder="$0.00"
                          />

                          <DatePicker
                            oneTap
                            format="yyyy-MM-dd"
                            value={parseMaybeIsoToDate(effVal)}
                            onChange={(v) =>
                              setEffectiveDateByUserId((prev) => ({
                                ...(prev || {}),
                                [userId]: normalizeIso10(v),
                              }))
                            }
                            placeholder="YYYY-MM-DD"
                            style={{ width: 170 }}
                          />
                        </Stack>

                        {!hasRate || !hasEff ? (
                          <div style={{ marginTop: 6, fontSize: 12, color: '#d4380d' }}>
                            {!hasRate ? 'Rate is required. ' : null}
                            {!hasEff ? 'Effective date is required.' : null}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>

              {!step4Valid ? (
                <div style={{ marginTop: 12 }}>
                  <Message type="warning" showIcon>
                    Enter a rate and effective date for each person.
                  </Message>
                </div>
              ) : null}

              <div className="ppc-project-wizard__footer">
                <WizardError error={upsertUserRate.error} />
                <Stack justifyContent="space-between" spacing={10}>
                  <Button appearance="subtle" onClick={() => setStep(3)} disabled={isBusy}>
                    Back
                  </Button>
                  <Button appearance="primary" onClick={submitStep5} loading={isBusy} disabled={!step4Valid || isBusy}>
                    Finish
                  </Button>
                </Stack>
              </div>
            </Panel>
          ) : null}
        </div>
      </Panel>

      <Modal
        open={addTaskOrderOpen}
        onClose={() => {
          setAddTaskOrderOpen(false);
          resetTaskOrderModal();
        }}
        size="sm"
      >
        <Modal.Header>
          <Modal.Title>{taskOrderModalMode === 'edit' ? 'Edit Task Order' : 'Add Task Order'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!Number.isInteger(safeProjectId) ? (
            <Message type="warning" showIcon>
              Create the project first.
            </Message>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div className="ppc-project-wizard__label">Task order name</div>
              <Input
                value={newTaskOrderName}
                onChange={setNewTaskOrderName}
                placeholder="e.g. TO-01"
              />
            </div>

            <div className="ppc-project-wizard__grid">
              <div>
                <div className="ppc-project-wizard__label">Start date</div>
                <DatePicker
                  oneTap
                  format="yyyy-MM-dd"
                  value={parseMaybeIsoToDate(newTaskOrderStartDate) || newTaskOrderStartDate}
                  onChange={(v) => {
                    setNewTaskOrderStartDate(v);
                    const s = normalizeIso10(v);
                    const e = normalizeIso10(newTaskOrderEndDate);
                    if (s && e && String(e).localeCompare(String(s)) < 0) {
                      setNewTaskOrderEndDate(v);
                    }
                  }}
                  shouldDisableDate={shouldDisableStartDate}
                  placeholder="YYYY-MM-DD"
                  style={{ width: '100%' }}
                />
                {projectStartIso && projectEndIso ? (
                  <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                    Project range: {projectStartIso} → {projectEndIso}
                  </div>
                ) : null}
              </div>

              <div>
                <div className="ppc-project-wizard__label">End date</div>
                <DatePicker
                  oneTap
                  format="yyyy-MM-dd"
                  value={parseMaybeIsoToDate(newTaskOrderEndDate) || newTaskOrderEndDate}
                  onChange={(v) => {
                    setNewTaskOrderEndDate(v);
                    const s = normalizeIso10(newTaskOrderStartDate);
                    const e = normalizeIso10(v);
                    if (s && e && String(e).localeCompare(String(s)) < 0) {
                      setNewTaskOrderStartDate(v);
                    }
                  }}
                  shouldDisableDate={shouldDisableEndDate}
                  placeholder="YYYY-MM-DD"
                  style={{ width: '100%' }}
                />
              </div>
            </div>

            <div>
              <div className="ppc-project-wizard__label">Budget</div>
              <Input
                className="ppc-project-wizard__budget-input"
                value={newTaskOrderBudget}
                onChange={handleNewTaskOrderBudgetChange}
                placeholder="$0"
                inputMode="numeric"
              />
              <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>
                Overall project budget:{' '}
                <span style={{ fontWeight: 700 }}>
                  {Number.isFinite(Number(budgetValueNumber)) ? budgetCurrencyFormatter(budgetValueNumber) : 'Not set'}
                </span>
              </div>
              {Number.isFinite(Number(remainingBudget)) ? (
                <div style={{ fontSize: 12, color: remainingAfterEntered !== null && remainingAfterEntered < 0 ? '#d4380d' : '#666', marginTop: 4 }}>
                  Remaining budget {taskOrderModalMode === 'edit' ? '(excluding this order)' : ''}:{' '}
                  <span style={{ fontWeight: 700 }}>
                    {budgetCurrencyFormatter(Math.max(0, Number(remainingBudget)))}
                  </span>
                  {Number.isFinite(Number(budgetEntered)) ? (
                    <>
                      {' '}
                      • After this:{' '}
                      <span style={{ fontWeight: 700 }}>
                        {budgetCurrencyFormatter(Math.max(0, Number(remainingAfterEntered ?? 0)))}
                      </span>
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>

            {!canSubmitNewTaskOrder ? (
              <Message type="info" showIcon>
                Enter a name, dates within the project range, and a budget.
              </Message>
            ) : null}

            {createTaskOrder.error ? <WizardError error={createTaskOrder.error} /> : null}
            {updateTaskOrder.error ? <WizardError error={updateTaskOrder.error} /> : null}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            appearance="primary"
            onClick={() => void submitNewTaskOrder()}
            loading={createTaskOrder.isPending || updateTaskOrder.isPending}
            disabled={
              !Number.isInteger(safeProjectId) ||
              !canSubmitNewTaskOrder ||
              createTaskOrder.isPending ||
              updateTaskOrder.isPending
            }
          >
            {taskOrderModalMode === 'edit' ? 'Save' : 'Add'}
          </Button>
          <Button
            appearance="subtle"
            onClick={() => {
              setAddTaskOrderOpen(false);
              resetTaskOrderModal();
            }}
            disabled={createTaskOrder.isPending || updateTaskOrder.isPending}
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
